import * as React from 'react';
import {
  Grid, Card, CardHeader, CardContent, CardActions, Box, Typography, Paper, TextField, Button, CircularProgress,
} from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import type { IMetalBin } from './MetalBinInterfaces';
import type { ILabelValuePair } from '../marking/MarkingInterfaces';
import getMetalTypes from '../../lib/SWRfetchers/getMetalTypes';

interface MetalBinAddProps {
  formValues: IMetalBin,
  setFormValues: (values: IMetalBin) => void,
  secondaryButtonAction: () => void,
  primaryButtonAction: () => void,
  interfaceType: 'Add' | 'Edit',
  loading: boolean,
}

const MetalBinAdd: React.FC<MetalBinAddProps> = ({
  formValues, setFormValues, secondaryButtonAction, primaryButtonAction, interfaceType, loading,
}) => {
  const { metalTypes } = getMetalTypes();

  const [ringSizes, setRingSizes] = React.useState<string[]>([]);

  function GetRange(start: number, stop: number, step: number) {
    return Array.from(
      { length: (stop - start) / step + 1 },
      (_, i) => start + (i * step),
    );
  }

  React.useEffect(() => {
    function GetRingSizes() {
      const rs = GetRange(0.25, 20, 0.25)
        .map(
          (e) => (e.toString()),
        );
      setRingSizes(rs);
    }
    GetRingSizes();
  }, []);

  return (
    <>
      <Paper>
        <Card>
          <CardHeader
            title={(
              <Typography variant="h6" display="inline">{ interfaceType === 'Add' ? 'Add Bin' : 'Edit Bin'}</Typography>
            )}
            disableTypography
          />
          <CardContent>
            <Grid container spacing={4}>
              <Grid item md={6} xs={12}>
                {
                  metalTypes ? (
                    <Autocomplete
                      value={formValues?.metalType || null}
                      onChange={(e, data: ILabelValuePair | null) => {
                        if (data) {
                          const tempFormValues: IMetalBin = { ...formValues, metalType: data };
                          setFormValues(tempFormValues);
                        } else {
                          const tempFormValues: IMetalBin = { ...formValues, metalType: null };
                          setFormValues(tempFormValues);
                        }
                      }}
                      autoHighlight
                      autoSelect
                      options={metalTypes.sort((a, b) => a.label.localeCompare(b.label))}
                      getOptionLabel={(option) => option.label.toString()}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                      renderInput={(params) => <TextField required {...params} id="metal-type-input" label="Metal Type" variant="outlined" />}
                    />
                  ) : (<Skeleton height="56px" variant="rect" />)
}
              </Grid>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  value={formValues?.ringThickness?.toString() || ''}
                  onChange={(e, data) => {
                    if (data) {
                      const inputVal: number = parseFloat(data.toString());
                      const tempFormValues: IMetalBin = { ...formValues, ringThickness: inputVal };
                      setFormValues(tempFormValues);
                    }
                  }}
                  autoHighlight
                  autoSelect
                  options={ringSizes}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField required {...params} id="ring-thickness-input" label="Ring Thickness" variant="outlined" />}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  value={formValues?.ringWidth?.toString() || ''}
                  onChange={(e, data) => {
                    if (data) {
                      const inputVal: number = parseFloat(data.toString());
                      const tempFormValues: IMetalBin = { ...formValues, ringWidth: inputVal };
                      setFormValues(tempFormValues);
                    }
                  }}
                  autoHighlight
                  autoSelect
                  options={ringSizes}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField required {...params} id="ring-width-input" label="Ring Width" variant="outlined" />}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  value={formValues?.sizeChange?.toString() || ''}
                  onChange={(e, data) => {
                    if (data) {
                      const inputVal: number = parseFloat(data.toString());
                      const tempFormValues: IMetalBin = { ...formValues, sizeChange: inputVal };
                      setFormValues(tempFormValues);
                    }
                  }}
                  autoHighlight
                  autoSelect
                  options={ringSizes}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField required {...params} id="change-in-size-input" label="Change in Size" variant="outlined" />}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Box py={1}>
                  <Typography variant="h6" display="inline">{interfaceType === 'Add' ? 'Assign to Metal Bin' : 'Reassign to Metal Bin'}</Typography>
                </Box>
                <Box py={1}>
                  <TextField
                    id="metal-bin-name-value-input"
                    value={formValues?.metalBinName ?? null}
                    label="Metal Bin Name"
                    variant="outlined"
                    type="number"
                    fullWidth
                    onChange={(e) => {
                      const data = e.target.value;
                      if (data) {
                        const inputVal: number = parseInt(data, 10);
                        const tempFormValues: IMetalBin = { ...formValues, metalBinName: inputVal };
                        setFormValues(tempFormValues);
                      } else {
                        const tempFormValues: IMetalBin = { ...formValues, metalBinName: null };
                        setFormValues(tempFormValues);
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Grid container direction="row-reverse" spacing={2}>
              <Grid item>
                <Button disabled={loading} color="primary" variant="contained" onClick={primaryButtonAction}>
                  {loading ? <CircularProgress size={25} /> : <>{ interfaceType === 'Add' ? 'ADD BIN' : 'EDIT BIN'}</> }
                </Button>
              </Grid>
              {
                interfaceType === 'Add' ? (
                  <Grid item>
                    <Button variant="text" color="primary" onClick={secondaryButtonAction}>{'RESET'}</Button>
                  </Grid>
                ) : (
                  <Grid item>
                    <Button variant="text" color="secondary" onClick={secondaryButtonAction}>{'CANCEL'}</Button>
                  </Grid>
                )
              }
            </Grid>
          </CardActions>
        </Card>
      </Paper>
    </>
  );
};
export default MetalBinAdd;
